import React from 'react';
import { Box, IconButton, Typography, Slider } from '@mui/material';
import {
  Close as CloseIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  VolumeDown as VolumeDownIcon,
  RecordVoiceOver as NarrationOnIcon,
  VoiceOverOff as NarrationOffIcon,
} from '@mui/icons-material';

export interface PlaybackControlsProps {
  currentIndex: number;
  totalOutfits: number;
  isPaused: boolean;
  isMusicMuted: boolean;
  isNarrationMuted: boolean;
  musicVolume: number;
  onExit: () => void;
  onToggleMusicMute: () => void;
  onToggleNarrationMute: () => void;
  onMusicVolumeChange: (volume: number) => void;
}

/**
 * Overlay controls rendered on top of the outfit image during reel playback.
 *
 * Layout:
 *  - Exit button: top-right corner
 *  - Music mute toggle: top-left area
 *  - Narration mute toggle: top-left area (next to music toggle)
 *  - Pause indicator: center of screen (visible only when paused)
 *  - Progress indicator: bottom center ("X of Y")
 *
 * All controls use pointer-events so they don't block the outfit slide click
 * area except on the interactive elements themselves.
 *
 * Requirements: 7.1, 3.5, 5.2, 8.4, 6.6, 10.9
 */
const PlaybackControls: React.FC<PlaybackControlsProps> = ({
  currentIndex,
  totalOutfits,
  isPaused,
  isMusicMuted,
  isNarrationMuted,
  musicVolume,
  onExit,
  onToggleMusicMute,
  onToggleNarrationMute,
  onMusicVolumeChange,
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {/* Top bar: mute toggles (left) and exit button (right) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: 2,
        }}
      >
        {/* Music volume slider + mute toggle, narration toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'auto' }}>
          <IconButton
            onClick={onToggleMusicMute}
            aria-label={isMusicMuted ? 'Unmute music' : 'Mute music'}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
            }}
            size="medium"
          >
            {isMusicMuted ? <VolumeOffIcon /> : (musicVolume < 0.4 ? <VolumeDownIcon /> : <VolumeUpIcon />)}
          </IconButton>

          <Slider
            value={isMusicMuted ? 0 : musicVolume}
            onChange={(_e, val) => onMusicVolumeChange(val as number)}
            min={0}
            max={1}
            step={0.05}
            aria-label="Music volume"
            sx={{
              width: 80,
              color: 'white',
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: 'white',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 6px rgba(255,255,255,0.2)',
                },
              },
              '& .MuiSlider-track': {
                height: 3,
              },
              '& .MuiSlider-rail': {
                height: 3,
                opacity: 0.4,
              },
            }}
          />

          <IconButton
            onClick={onToggleNarrationMute}
            aria-label={isNarrationMuted ? 'Unmute narration' : 'Mute narration'}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
            }}
            size="medium"
          >
            {isNarrationMuted ? <NarrationOffIcon /> : <NarrationOnIcon />}
          </IconButton>
        </Box>

        {/* Exit button */}
        <IconButton
          onClick={onExit}
          aria-label="Exit fashion show"
          sx={{
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
            pointerEvents: 'auto',
          }}
          size="medium"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Pause indicator — center of screen, only visible when paused */}
      {isPaused && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <PauseIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>
      )}

      {/* Progress indicator — bottom center */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          pb: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: 2,
            px: 2,
            py: 0.5,
            fontWeight: 500,
            letterSpacing: '0.05em',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {currentIndex + 1} of {totalOutfits}
        </Typography>
      </Box>
    </Box>
  );
};

export default PlaybackControls;
